import React, { useEffect, useMemo, useState } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { RADAR_AXES } from "../utils/genieMapping";
import { CHART, COLORS } from "theme/tokens";

// ──────────────────────────────────────────────────────────────────────────
// 動畫鎖：module-level Set，跨 mount/unmount/scroll resize 都保留狀態
// 每個 RadarChart6 instance 用 caller 傳的 instanceKey (如 elite.id) 區分
// 第一次顯示動畫跑完後存入 Set，下次同 key 永遠不動畫
// 即使 React StrictMode / ResponsiveContainer resize 觸發 remount 也擋得住
// ──────────────────────────────────────────────────────────────────────────
const animatedInstances = new Set();

// 六角雷達圖
//   values: { return, defense, stability, burst, winRate, adaptability } 0~100
//           ⚠️ 任何軸可能是 null（後端 dataQuality 旗標說沒資料）：
//              畫面用「—」標示、面積計算當 0 處理（避免把多邊形拉歪）
//   color:  主色（hex），預設 indigo
//   variant: 'full' (帶軸標籤+數值) | 'mini' (只有圖形，無標籤)
//   instanceKey: 動畫鎖用（建議傳 elite.id）；沒傳則每次 mount 都重新動畫
export default function RadarChart6({
  values,
  color = "#6366f1",
  variant = "full",
  height = 260,
  instanceKey,
  labelFontSize = 14,   // 軸標籤字級（my-strategies 卡片用小版傳 11）
  dotRadius = 13,       // vertex 圓圈半徑
  dotFontSize = 11,     // vertex 數字字級
  outerRadius,          // 多邊形外半徑（不傳則 full 78% / mini 80%）；調大可讓雷達填滿 wrapper
  axes = RADAR_AXES,    // 可傳入在地化軸標籤 [{key,label}]；預設用 genieMapping 的中文
  axisInfo = null,      // { [axisKey]: { param, desc } }：有傳則每個軸標籤可點 → 跳該軸自己的依據 tooltip（detail modal 用）
  basedOn = '',         // 「依據」連接詞（隨語系）
}) {
  // 初始 state 從 module-level Set 讀：已動畫過的 instance 直接設 true
  const [hasAnimated, setHasAnimated] = useState(
    () => !!(instanceKey && animatedInstances.has(instanceKey))
  );
  // per-axis tooltip：目前展開哪個軸（{ key, label, x, y }）；axisInfo 有傳才啟用
  const [activeAxis, setActiveAxis] = useState(null);

  useEffect(() => {
    if (hasAnimated) return;
    // 第一次顯示：1500ms 動畫結束後鎖到 module-level Set
    const t = setTimeout(() => {
      if (instanceKey) animatedInstances.add(instanceKey);
      setHasAnimated(true);
    }, 1500);
    return () => clearTimeout(t);
  }, [hasAnimated, instanceKey]);

  // data 用 useMemo cache 避免 recharts 看到 reference 變化重啟動畫
  const data = useMemo(
    () => axes.map((a) => {
      const raw = values?.[a.key];
      const isMissing = raw == null;
      return {
        axis: a.label,
        value: isMissing ? 0 : Number(raw),
        isMissing,
      };
    }),
    [values, axes]
  );

  const isMini = variant === "mini";
  const isAnimationActive = !hasAnimated && !isMini;

  // 軸標籤（外圍）— 只顯示「報酬力」等文字，數字改顯示在 vertex 圓圈內
  const renderTick = (props) => {
    const { payload, x, y, textAnchor } = props;
    const axisKey = axes.find((a) => a.label === payload.value)?.key;
    const info = axisKey ? axisInfo?.[axisKey] : null;
    // per-axis：每個軸標籤旁放一顆橘 ⓘ（外側：左側軸放前面、其餘放後面），
    //   點整段 → 跳該軸 tooltip；stopPropagation 避免冒泡到「整張卡點擊→開 modal」
    const onPick = info
      ? (e) => { e.stopPropagation(); setActiveAxis((prev) => (prev && prev.key === axisKey ? null : { key: axisKey, label: payload.value, x, y })); }
      : undefined;
    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        fill={CHART.radarLabelColor}
        fontSize={labelFontSize}
        fontWeight={CHART.radarLabelWeight}
        style={info ? { cursor: 'pointer' } : undefined}
        onClick={onPick}
      >
        {info && textAnchor === 'end' && <tspan fill={color} fontWeight={700}>ⓘ </tspan>}
        {payload.value}
        {info && textAnchor !== 'end' && <tspan dx={3} fill={color} fontWeight={700}>ⓘ</tspan>}
      </text>
    );
  };

  // 每個 vertex 的自定 dot：白底 + banner color border + banner color 數字
  // recharts Radar dot prop 傳的 props 含 cx/cy/value/payload；
  // payload 就是 data row（{axis, value, isMissing}）所以直接讀
  const renderDot = (props) => {
    const { cx, cy, payload, value } = props;
    if (cx == null || cy == null) return null;
    const isMissing = payload?.isMissing;
    const displayValue = isMissing ? '—' : Math.round(value ?? payload?.value ?? 0);
    const dotColor = isMissing ? '#94a3b8' : color;
    return (
      <g>
        <circle cx={cx} cy={cy} r={dotRadius} fill={COLORS.white} stroke={dotColor} strokeWidth={2} />
        <text
          x={cx}
          y={cy}
          dy={dotFontSize * 0.36}
          textAnchor="middle"
          fill={dotColor}
          fontSize={dotFontSize}
          fontWeight={800}
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {displayValue}
        </text>
      </g>
    );
  };

  const activeInfo = activeAxis ? axisInfo?.[activeAxis.key] : null;
  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
      <RadarChart
        data={data}
        outerRadius={outerRadius || (isMini ? "80%" : "78%")}
        margin={isMini ? { top: 4, right: 4, bottom: 4, left: 4 } : { top: 16, right: 32, bottom: 16, left: 32 }}
      >
        <PolarGrid stroke="#e2e8f0" gridType="polygon" />
        {!isMini && (
          <PolarAngleAxis dataKey="axis" tick={renderTick} />
        )}
        {isMini && (
          <PolarAngleAxis dataKey="axis" tick={false} />
        )}
        <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
        <Radar
          name="strategy"
          dataKey="value"
          stroke={color}
          fill={color}
          fillOpacity={0.32}
          strokeWidth={2}
          isAnimationActive={isAnimationActive}
          dot={isMini ? false : renderDot}
          activeDot={false}
        />
      </RadarChart>
      </ResponsiveContainer>
      {activeAxis && activeInfo && (
        <div
          style={{
            position: 'absolute', left: activeAxis.x, top: activeAxis.y, zIndex: 20, pointerEvents: 'none',
            transform: activeAxis.y < height * 0.5 ? 'translate(-50%, 14px)' : 'translate(-50%, calc(-100% - 14px))',
            width: 'max-content', maxWidth: 190, background: '#FFF', borderRadius: 10,
            border: '1px solid #EFEFEF', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: '9px 12px',
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#2C2C2C' }}>
            {activeAxis.label}
            <span style={{ color: '#9A9A9A', fontWeight: 400 }}> — {basedOn} </span>
            <span style={{ color, fontWeight: 700 }}>{activeInfo.param}</span>
          </div>
          <div style={{ fontSize: 11, color: '#8A8A8A', marginTop: 1 }}>{activeInfo.desc}</div>
        </div>
      )}
    </div>
  );
}
